import * as React from 'react';
import { useState } from 'react';

import { translate } from '~l10n/client';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { sendTeacherIntercomEvent } from '~analytics/intercom';
import { EventTypes } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import {
  Dialog,
  DialogConfirmation,
  DialogTitle,
} from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { ErrorDialog } from '~teacher/components/ErrorDialog';
import { useAccount } from '~teacher/hook/account';
import { useAcceptTeamInvite, useGetTeamInviteInfo } from '~teacher/hook/team';

import { isAxiosError } from '../../../api/isAxiosError';
import { sleep } from '../../../util/sleep';

const MAX_TEAM_LIMIT = 3;

interface JoinTeamDialogProps {
  inviteCode: string;
}

export function JoinTeam({ inviteCode }: JoinTeamDialogProps) {
  if (inviteCode === 'error') {
    return <JoinTeamExpiredErrorDialog />;
  } else {
    return <JoinTeamDialog inviteCode={inviteCode} />;
  }
}

export function JoinTeamDialog({ inviteCode }: JoinTeamDialogProps) {
  const invite = useGetTeamInviteInfo(inviteCode);

  // If there is no invite code, do nothing
  if (!inviteCode) {
    return null;
  }

  // If there is no invite or it's expired
  if (invite.error) {
    return <JoinTeamErrorDialog />;
  }

  if (!invite.data) {
    return null;
  }

  const { inviterName, teamId, email } = invite.data;

  sendTeacherAmplitudeEvent(EventTypes.TeamsDisplayInvite, {
    team_id: teamId,
  });

  return (
    <JoinTeamDialogData
      inviteCode={inviteCode}
      teamId={teamId}
      inviteEmail={email}
      inviterName={inviterName}
    />
  );
}

interface JoinTeamDialogDataProps {
  inviteCode: string;
  teamId: string;
  inviteEmail: string;
  inviterName: string;
}

export function JoinTeamDialogData({
  inviteEmail,
  teamId,
  inviteCode,
  inviterName,
}: JoinTeamDialogDataProps) {
  const account = useAccount({
    select: (accountResponse) => accountResponse.email,
  });
  const [isOpen, setIsOpen] = useState(true);
  const acceptTeamInviteMutation = useAcceptTeamInvite();

  const onClose = () => {
    setIsOpen(false);
  };

  if (!account.data) return null;

  if (account.data !== inviteEmail) {
    return (
      <DialogConfirmation
        id="unmatching-email-dialog"
        closeLabel={translate('Close')}
        open={isOpen}
        onClose={onClose}
        title={translate('Unable to Join Team')}
        actions={[
          <UIButton key="ok" onClick={onClose}>
            {translate('Ok')}
          </UIButton>,
        ]}
      >
        <div className="grid gap-8">
          <p>
            {translate(
              'To join, you must create an account or sign in using the email on the invite.'
            )}
          </p>
          <p
            dangerouslySetInnerHTML={{
              __html: translate('You are currently signed in as {0}').format(`
                <span class="font-700">${account.data}.</span>`),
            }}
          ></p>
        </div>
      </DialogConfirmation>
    );
  }

  switch (acceptTeamInviteMutation.status) {
    case 'error':
      if (isAxiosError(acceptTeamInviteMutation.error)) {
        const error = acceptTeamInviteMutation.error;
        if (error.response?.data?.code === 'maximum_team_limit') {
          return <MaxTeamLimitReachedDialog />;
        } else if (error.response?.data?.code === 'user_already_member') {
          return null;
        } else if (error.response?.data?.code === 'invite_not_found') {
          return <JoinTeamExpiredErrorDialog />;
        } else {
          return <JoinTeamErrorDialog />;
        }
      } else {
        return <JoinTeamErrorDialog />;
      }
      break;
    case 'loading':
    case 'success':
    case 'idle': {
      return (
        <DialogConfirmation
          closeLabel={translate('Close')}
          open={isOpen}
          onClose={onClose}
          id="accept-invite-dialog"
          title={translate('Join Team on Socrative')}
          actions={[
            <UIButton
              key="primary"
              appearance="primary"
              isLoading={
                acceptTeamInviteMutation.status === 'loading' ||
                acceptTeamInviteMutation.status === 'success'
              }
              onClick={() => {
                acceptTeamInviteMutation.mutate(
                  { teamId: teamId, inviteId: inviteCode },
                  {
                    onSuccess: async () => {
                      toast.positive(translate('Join team confirmed'));

                      sendTeacherAmplitudeEvent(EventTypes.TeamsAcceptInvite, {
                        team_id: teamId,
                      });
                      sendTeacherIntercomEvent(EventTypes.TeamsAcceptInvite);

                      onClose();
                      await sleep(3000);
                      window.location.reload();
                    },
                  }
                );
              }}
            >
              {translate('Join Team')}
            </UIButton>,
          ]}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: translate(
                '{0} invited you to join their team on Socrative.'
              ).format(`<span class="font-700">${inviterName}</span>`),
            }}
          />
          <div>
            {translate(
              'By joining a team, you consent to sharing your name and email with all team members.'
            )}
          </div>
        </DialogConfirmation>
      );
    }
  }
}

/**
 * Error message that shows when an invitation is no longer valid.
 */
function JoinTeamExpiredErrorDialog() {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <DialogConfirmation
      closeLabel={translate('Close')}
      open={isOpen}
      onClose={() => setIsOpen(false)}
      id="accept-invite-error-dialog"
      title={translate('Invalid or Expired Invite')}
      actions={[
        <UIButton key="ok" onClick={() => setIsOpen(false)}>
          {translate('Ok')}
        </UIButton>,
      ]}
    >
      {translate(
        'The link has expired, is invalid, or has already been used. Please request a new invite or check that the URL is correct and try again.'
      )}
    </DialogConfirmation>
  );
}

function MaxTeamLimitReachedDialog() {
  const [isOpen, setIsOpen] = useState(true);

  const onClose = () => {
    setIsOpen(false);
  };

  return (
    <Dialog
      padding="confirmation"
      id="max-team-limit-dialog"
      closeLabel={translate('Close')}
      open={isOpen}
      onClose={onClose}
      title={translate('Unable to Join Team')}
    >
      {(headerProps) => (
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-8">
            <h3 className="m-0 text-left">
              <DialogTitle
                ref={headerProps.headingRef}
                style={{ paddingRight: 0 }}
              >
                {headerProps.title}
              </DialogTitle>
            </h3>
            <div className="text-base text-blue-900 leading-8">
              <div className="grid gap-8">
                <p className="text-left">
                  {translate(
                    "You've joined the maximum of {0} teams. Leave a team to join a new one."
                  ).format(MAX_TEAM_LIMIT)}
                </p>
                <a
                  className="text-left text-base no-underline leading-8 font-sans font-600 text-cyan-700 hover:text-cyan-800"
                  href="https://help.socrative.com/en/articles/9902881-joining-or-leaving-a-team"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {translate('Learn More')}
                </a>
              </div>
            </div>
          </div>
          <div className="flex justify-end -mx-4">
            <UIButton onClick={onClose}>{translate('Ok')}</UIButton>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function JoinTeamErrorDialog() {
  const [isOpen, setIsOpen] = useState(true);
  return <ErrorDialog open={isOpen} onClose={() => setIsOpen(false)} />;
}
